import { debounce } from '@mui/material';
import axios, { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';

export interface Geometry {
  type: 'Point';
  coordinates: [number, number];
}

export interface GeoJsonFeature {
  type: 'Feature';
  geometry: Geometry;
  properties: {
    locale: null | string;
    geoEntityType: null | GeoEntity;
    country: null | string;
    state: null | string;
    neighbourhood: null | string;
    district: null | string;
    county: null | string;
    city: null | string;
    cityPart: null | string;
    zip: null | string;
    street: null | string;
    village: null | string;
    houseNo: null | string;
    geometry: Geometry;
    poi: null | string;
    _osmid: number;
    repositoryScore: number;
    displayValue: string;
  };
}
/*
 * more request options at: https://docs.yellowmap.com/smartmaps-backend/anleitung/backend-api-v5-autocomplete-guidedsearch-post-v5/
 */
export interface GeoRequestOptions {
  query?: string;
  boundingbox?: {
    leftDown: {
      latitude: number;
      longitude: number;
    };
    rightUp: {
      latitude: number;
      longitude: number;
    };
  };
  center?: {
    latitude: number;
    longitude: number;
  };
  filterOptions?: {
    city?: string;
    village?: string;
    cityPart?: string;
    country?: string;
    county?: string;
    district?: string;
    neighbourhood?: string;
    state?: string;
    street?: string;
    zip?: string;
    searchField?: string;
    poi?: string;
    strictFieldSearch?: boolean;
    includedGeoEntities?: Array<GeoEntity>;
    excludedGeoEntities?: Array<GeoEntity>;
    inBoundingBox?: boolean;
  };
}

export interface FetchOptions {
  fetchToken?: boolean;
}

export interface PropsConfig {
  yellowmapApiUrl: string;
  yellowmapAutocompleteUrl: string;
  yellowmapSystemPartner: string;
  yellowmapApiKey: string;
}

export type GeoEntity =
  | 'UNDEFINED'
  | 'AIRPORT'
  | 'CITY'
  | 'CITY_WITH_ZIP'
  | 'CITYPART'
  | 'CITYPART_WITH_ZIP'
  | 'COUNTY'
  | 'COUNTRY'
  | 'DISTRICT'
  | 'NEIGHBOURHOOD'
  | 'STATE'
  | 'STREET'
  | 'STREET_WITHOUT_CITY'
  | 'TRAIN_STATION'
  | 'ZIP'
  | 'ISLAND'
  | 'VILLAGE'
  | 'ATTRACTION';

interface Result {
  loading: boolean;
  error: boolean;
  features: GeoJsonFeature[];
}

export const useYellowMapsAutoComplete = (
  propConfig: PropsConfig,
  requestOptions: GeoRequestOptions,
  fetchOptions?: FetchOptions
): Result => {
  const [loading, setLoading] = useState(!!requestOptions.query);
  const [token, setToken] = useState(null);
  const [features, setFeatures] = useState<GeoJsonFeature[]>([]);
  const [error, setError] = useState<boolean>(false);
  const { yellowmapApiUrl, yellowmapAutocompleteUrl, yellowmapSystemPartner, yellowmapApiKey } = propConfig;

  const yellowmapTokenUrl = `${yellowmapApiUrl}/api_rst/v2/yellowmapsapi/authtoken/autocompleteToken?systemPartner=${yellowmapSystemPartner}&securityID=${yellowmapApiKey}`;

  useEffect(() => {
    let active = true;
    const fetchToken = async () => {
      if (token || fetchOptions?.fetchToken === false) {
        return;
      }
      let newToken = null;
      try {
        setLoading(true);
        const { data } = await axios.get(yellowmapTokenUrl);
        newToken = data;
      } catch {
        newToken = null;
      }
      if (active) {
        setToken(newToken);
        setError(!newToken);
      }
    };
    fetchToken();
    return () => {
      active = false;
    };
  }, [token, yellowmapTokenUrl, fetchOptions?.fetchToken]);

  useEffect(() => {
    debounceQuery(token, requestOptions);
  }, [token, requestOptions.query]);

  const debounceQuery = useRef(
    debounce((token, requestOptions) => {
      if (requestOptions.query === undefined || requestOptions.query.length < 3) {
        setFeatures([]);
        return;
      }

      const fetchAddressesV5 = async (requestOptions: GeoRequestOptions) => {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        };
        const defaultRequestData = {
          geoJson: true,
          boostOptions: {
            proximityBoost: {
              radius: 1000,
              value: 1,
            },
          },
          center: {
            latitude: 51,
            longitude: 10,
          },
          boundingbox: {},
          isoCountries: ['de'],
        };

        let searchResult = null;
        try {
          const result = await axios.post(
            yellowmapAutocompleteUrl,
            { ...defaultRequestData, ...requestOptions },
            config
          );
          searchResult = result.data;
        } catch (error: unknown) {
          if (
            active &&
            ((error as AxiosError).response?.status === 412 || (error as AxiosError).response?.status === 401)
          ) {
            setToken(null);
          }
        }
        if (active) {
          setFeatures(searchResult?.features || []);
          setError(!searchResult);
          setLoading(false);
        }
      };
      let active = true;
      if (token && requestOptions) {
        fetchAddressesV5(requestOptions);
      }
      return () => {
        active = false;
      };
    }, 500)
  ).current;

  return { loading, error, features };
};
